import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, Button, Input } from '../components';
import { useCourriers } from '../hooks';
import { queryKeys } from '../lib/react-query';
import { CourrierStatus, CourrierType, CourrierPriority } from '../types/api';

export function Courriers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: '' as CourrierStatus | '',
    type: '' as CourrierType | '',
    priority: '' as CourrierPriority | '',
    q: '',
    searchInPieces: true,
  });
  
  const [initialFilters, setInitialFilters] = useState({
    status: '' as CourrierStatus | '',
    type: '' as CourrierType | '',
    priority: '' as CourrierPriority | '',
    q: '',
    searchInPieces: true,
  });

  // Effet pour initialiser les filtres depuis les paramètres d'URL
  useEffect(() => {
    const urlPriority = searchParams.get('priority');
    const urlStatus = searchParams.get('status');
    const urlType = searchParams.get('type');
    const urlQuery = searchParams.get('q');

    const newFilters = {
      priority: (urlPriority as CourrierPriority) || '',
      status: (urlStatus as CourrierStatus) || '',
      type: (urlType as CourrierType) || '',
      q: urlQuery || '',
      searchInPieces: true, // Par défaut, rechercher dans les pièces
    };
    
    setFilters(newFilters as typeof filters);
    setInitialFilters(newFilters as typeof filters);
  }, [searchParams]);

  const {
    courriers,
    isLoading,
    error,
    refreshCourriers,
    searchCourriers,
  } = useCourriers({
    filters: Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '')
    ),
    autoLoad: true,
  });

  // Debug logs
  console.log('📄 [COURRIERS PAGE] Rendu de la page:', {
    courriersCount: courriers?.length || 0,
    isLoading,
    error,
    filters,
    courriers: courriers
  });

  const handleFilterChange = (field: string, value: string | boolean) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleSearch = () => {
    // Mettre à jour les paramètres d'URL
    const newSearchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      if (val && typeof val === 'string') newSearchParams.set(key, val);
    });
    setSearchParams(newSearchParams);
    
    searchCourriers(filters.q, {
      status: filters.status || undefined,
      type: filters.type || undefined,
      priority: filters.priority || undefined,
      searchInPieces: filters.searchInPieces,
    });
    
    // Mettre à jour les filtres initiaux après application
    setInitialFilters(filters);
  };

  // Effectuer une recherche automatique si des filtres sont présents dans l'URL
  useEffect(() => {
    const hasFilters = Object.values(filters).some(value => value !== '');
    if (hasFilters) {
      handleSearch();
    }
  }, []); // Seulement au montage initial

  // Vérifier si des filtres ont été modifiés
  const hasFilterChanges = JSON.stringify(filters) !== JSON.stringify(initialFilters);
  const hasActiveFilters = Object.values(filters).some(value => value !== '');
  
  // Déterminer l'état du bouton filtrer
  const getFilterButtonVariant = () => {
    if (hasActiveFilters && !hasFilterChanges) {
      return "filter-applied"; // Orange-to-purple inactif quand filtre appliqué
    } else if (hasFilterChanges) {
      return "gradient"; // Purple-to-pink actif quand il y a des changements
    } else {
      return "filter-inactive"; // Grisé inactif au départ
    }
  };
  
  const isFilterButtonDisabled = () => {
    return !hasFilterChanges; // Désactivé si pas de changements
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: '' as CourrierStatus | '',
      type: '' as CourrierType | '',
      priority: '' as CourrierPriority | '',
      q: '',
      searchInPieces: true,
    };
    setFilters(clearedFilters);
    setInitialFilters(clearedFilters);
    setSearchParams(new URLSearchParams());
    
    // Forcer le rechargement avec des filtres vides en utilisant searchCourriers
    // pour s'assurer que les filtres sont bien mis à jour dans le hook
    searchCourriers('', {});
  };

  const getStatusColor = (status: CourrierStatus) => {
    const colors = {
      enregistre: 'bg-blue-100 text-blue-800',
      recu: 'bg-blue-100 text-blue-800',
      en_validation: 'bg-yellow-100 text-yellow-800',
      incomplet: 'bg-red-100 text-red-800',
      en_attente_signature: 'bg-purple-100 text-purple-800',
      signe: 'bg-green-100 text-green-800',
      envoye: 'bg-indigo-100 text-indigo-800',
      classe: 'bg-gray-100 text-gray-800',
      rejete: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: CourrierPriority) => {
    const colors = {
      basse: 'bg-gray-100 text-gray-800',
      normale: 'bg-blue-100 text-blue-800',
      haute: 'bg-orange-100 text-orange-800',
      urgente: 'bg-red-100 text-red-800',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
        <div className="space-y-6 px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Courriers
              </h1>
              <p className="text-sm text-gray-600">
                Gestion et suivi documentaire
              </p>
            </div>
            <div>
              <Button
                onClick={() => navigate('/courriers/create')}
                variant="gradient"
                size="md"
                className="w-full sm:w-auto"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nouveau courrier
              </Button>
            </div>
          </div>

          {/* Filtres */}
          <div>
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <Input
                        placeholder="Rechercher..."
                        value={filters.q}
                        onChange={(e) => handleFilterChange('q', e.target.value)}
                        className="h-10"
                        icon={
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        }
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="searchInPieces"
                        checked={filters.searchInPieces}
                        onChange={(e) => handleFilterChange('searchInPieces', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="searchInPieces" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                        Rechercher dans les pièces jointes
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">

                    <select
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">Tous les statuts</option>
                      <option value="enregistre">Enregistré</option>
                      <option value="recu">Reçu</option>
                      <option value="en_validation">En validation</option>
                      <option value="incomplet">Incomplet</option>
                      <option value="en_attente_signature">En attente signature</option>
                      <option value="signe">Signé</option>
                      <option value="envoye">Envoyé</option>
                      <option value="classe">Classé</option>
                      <option value="rejete">Rejeté</option>
                    </select>

                    <select
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={filters.type}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                      <option value="">Tous les types</option>
                      <option value="entrant">Entrant</option>
                      <option value="sortant">Sortant</option>
                    </select>

                    <select
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={filters.priority}
                      onChange={(e) => handleFilterChange('priority', e.target.value)}
                    >
                      <option value="">Toutes les priorités</option>
                      <option value="basse">Basse</option>
                      <option value="normale">Normale</option>
                      <option value="haute">Haute</option>
                      <option value="urgente">Urgente</option>
                    </select>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={handleSearch}
                        variant={getFilterButtonVariant()}
                        size="sm"
                        className="h-10 w-full sm:w-auto"
                        disabled={isFilterButtonDisabled()}
                      >
                        Filtrer
                      </Button>
                      <Button onClick={handleClearFilters} variant="outline" size="sm" className="h-10 w-full sm:w-auto">
                        Effacer
                      </Button>
                      </div>
                    </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Liste des courriers */}
          <div>
            <Card className="bg-white">
              <CardContent>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="text-sm text-red-600 flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>{error}</span>
                    </div>
                  </div>
                )}

                {isLoading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="animate-pulse bg-gray-100 rounded-lg p-6">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    ))}
                  </div>
                ) : (courriers && courriers.length > 0) ? (
                  <div className="space-y-4">
                    {courriers.map((courrier) => (
                      <div
                        key={courrier.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex-1">
                          <div className="mb-1">
                            <h3 className="text-base font-semibold text-gray-900 break-words">
                              {courrier.subject}
                            </h3>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(courrier.status)}`}>
                              {courrier.status.replace('_', ' ')}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(courrier.priority)}`}>
                              {courrier.priority}
                            </span>
                          </div>
                          <div className="hidden sm:flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                            <span>📤 De: {courrier.sender}</span>
                            <span>📥 À: {courrier.recipient}</span>
                            <span>🏷️ Réf: {courrier.reference}</span>
                          </div>
                          {courrier.summaryText && (
                            <p className="mt-1 text-xs text-gray-600 italic">
                              "{courrier.summaryText.substring(0, 150)}..."
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto mt-3 sm:mt-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/courriers/${courrier.id}`)}
                            className="hover:bg-blue-50 hover:border-blue-300 text-xs w-full sm:w-auto"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Voir
                          </Button>
                          <Button
                            variant="gradient"
                            size="sm"
                            onClick={() => navigate(`/courriers/${courrier.id}`)}
                            className="text-xs w-full sm:w-auto"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifier
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Aucun courrier
                    </h3>
                    <p className="text-gray-500">
                      {filters.q || filters.status || filters.type || filters.priority
                        ? 'Aucun courrier ne correspond aux filtres.'
                        : 'Commencez par créer votre premier courrier.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
  );
}